import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';
import {PtLuxonDate} from '../../models/projects/date.models';
import {DataChangeNotification, SignalRService} from '../signalR/signalRService';
import {VesselContextService} from '../vessel/vessel-context.service';
import {VesselService} from '../vessel/vessel.service';
import {NavHelperService} from '../nav/nav-helper.service';

export interface DailyLogConcurrentEditHost {
  dailyLogDate: PtLuxonDate;
  reloadDailyLogAfterExternalChange(): Promise<void>;
}

/** Component-scoped coordinator for Daily Log realtime invalidation. */
@Injectable()
export class DailyLogConcurrentEditCoordinator {
  externalChangePending = false;
  pullingLatestChanges = false;
  externalChangeError = '';

  private host: DailyLogConcurrentEditHost;
  private changeSubscription: Subscription;
  private latestRevision: string | null = null;
  private vesselId: number | null = null;

  constructor(
    private signalRService: SignalRService,
    private vesselContextService: VesselContextService,
    private vesselService: VesselService,
    private navHelperService: NavHelperService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  initialize(host: DailyLogConcurrentEditHost): void {
    this.host = host;
  }

  async start(): Promise<void> {
    if (!this.host) {
      return;
    }

    this.subscribeToChanges();
    try {
      await this.resolveVesselId();
      await this.signalRService.connect(this.vesselId);
    } catch (error) {
      console.error('Unable to connect to Daily Log change notifications', error);
    }
  }

  stop(): void {
    if (this.changeSubscription) {
      this.changeSubscription.unsubscribe();
      this.changeSubscription = null;
    }
  }

  destroy(): void {
    this.stop();
    this.clearExternalChange();
    this.host = null;
  }

  resetForDateChange(): void {
    this.clearExternalChange();
  }

  async pullLatestChanges(): Promise<void> {
    if (!this.externalChangePending || this.pullingLatestChanges || !this.host?.dailyLogDate?.dateTime) {
      return;
    }

    const revisionAtStart = this.latestRevision;
    const dateAtStart = this.getLoadedDailyLogDate();
    this.pullingLatestChanges = true;
    this.externalChangeError = '';

    try {
      await this.host.reloadDailyLogAfterExternalChange();
      if (this.latestRevision === revisionAtStart && this.getLoadedDailyLogDate() === dateAtStart) {
        this.clearExternalChange();
      }
    } catch (error) {
      this.externalChangeError = 'The latest changes could not be loaded. Please try again.';
      console.error('Unable to reload the Daily Log after an external change', error);
    } finally {
      this.pullingLatestChanges = false;
    }
  }

  private subscribeToChanges(): void {
    if (this.changeSubscription && !this.changeSubscription.closed) {
      return;
    }

    this.changeSubscription = this.signalRService.changes$.subscribe(notification => {
      if (!this.isRelevantNotification(notification)) {
        return;
      }

      this.latestRevision = notification.revision;
      this.externalChangePending = true;
      this.externalChangeError = '';
      this.navHelperService.updateDailyLogStale(true);
      this.blurActiveControl();
    });
  }

  private isRelevantNotification(notification: DataChangeNotification): boolean {
    if (!notification || !this.host?.dailyLogDate?.dateTime) {
      return false;
    }

    return Number(notification.vesselId) === Number(this.vesselId) &&
      notification.area === 'DailyLog' &&
      notification.entityType === 'DailyLogDataSet' &&
      notification.changeType === 'Updated' &&
      notification.clientInstanceId !== this.signalRService.clientInstanceId &&
      (notification.entityId === 'all' || notification.entityId === this.getLoadedDailyLogDate());
  }

  private getLoadedDailyLogDate(): string | null {
    return this.host?.dailyLogDate?.dateTime
      ? this.host.dailyLogDate.dateTime.toFormat('yyyy-MM-dd')
      : null;
  }

  private async resolveVesselId(): Promise<void> {
    this.vesselId = this.vesselContextService.getVesselId();
    if (this.vesselId) {
      return;
    }

    const vessel = await this.vesselService.getEditor();
    this.vesselId = vessel && vessel.id;
    if (this.vesselId) {
      this.vesselContextService.setVesselId(this.vesselId);
    }
  }

  private clearExternalChange(): void {
    const wasPending = this.externalChangePending;
    this.externalChangePending = false;
    this.latestRevision = null;
    this.externalChangeError = '';
    if (wasPending) {
      this.navHelperService.updateDailyLogStale(false);
    }
  }

  private blurActiveControl(): void {
    const activeElement = this.document.activeElement as HTMLElement;
    if (activeElement && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/daily-log/daily-log-concurrent-edit-coordinator.service.ts